import { useMemo, useCallback } from 'react';
import { useSyncExternalStore } from 'react';

// Subscribe to URL changes
function subscribeToSearch(callback: () => void) {
  window.addEventListener('popstate', callback);
  return () => window.removeEventListener('popstate', callback);
}

function getSearchSnapshot() {
  return window.location.search;
}

/**
 * Compatibility hook that provides search params.
 * This provides a similar API to Next.js useSearchParams() for easier migration.
 */
export function useSearchParams(): URLSearchParams {
  const searchString = useSyncExternalStore(subscribeToSearch, getSearchSnapshot, getSearchSnapshot);
  return useMemo(() => new URLSearchParams(searchString), [searchString]);
}

/**
 * Hook to update search params while preserving existing ones.
 * Uses window.history directly to avoid TanStack Router type complexity.
 */
export function useUpdateSearchParams() {
  const set = useCallback((key: string, value: string) => {
    const params = new URLSearchParams(window.location.search);
    params.set(key, value);
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState(null, '', newUrl);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);

  const remove = useCallback((key: string) => {
    const params = new URLSearchParams(window.location.search);
    params.delete(key);
    const search = params.toString();
    const newUrl = search ? `${window.location.pathname}?${search}` : window.location.pathname;
    window.history.replaceState(null, '', newUrl);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);

  const update = useCallback((updates: Record<string, string | null>) => {
    const params = new URLSearchParams(window.location.search);
    for (const [key, value] of Object.entries(updates)) {
      if (value === null) {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    }
    const search = params.toString();
    const newUrl = search ? `${window.location.pathname}?${search}` : window.location.pathname;
    window.history.replaceState(null, '', newUrl);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }, []);

  return useMemo(() => ({ set, delete: remove, update }), [set, remove, update]);
}
